import {
    createBatchShipment,
    createWorkOrderBatch,
    getReplenishmentTrackOverview,
    getShipmentSuggestions,
    type TrackMother,
    type TrackOrigin,
    type TrackOverview,
    type TrackProductionOrder,
    type TrackShipment,
    type TrackStn,
    type TrackStnItem,
    type TrackWorkOrder,
} from '@api/replenishment-planning';
import { getMotherOrder, type MotherOrder } from '@api/mother-order';
import { AddShipmentModal } from '@components/containers/orders';
import {
    DataSheet,
    type DataSheetColumn,
} from '@components/ui/DataSheet';
import { DateSinglePicker } from '@components/ui/DateRange';
import {
    ActionIcon,
    Badge,
    Button,
    Card,
    Divider,
    Drawer,
    Group,
    Loader,
    Modal,
    NumberInput,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core';
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type FC,
    type ReactElement,
} from 'react';
import { formatCount } from '@utils/currency';

/**
 * Track rows are two-level: the instrument's header and the lines beneath it. A discriminated
 * union rather than optional fields, so a cell renderer can't read a line's quantity off a
 * header row by accident.
 */
type ProductionRow =
    | {
          rowKey: string;
          isHeader: true;
          order: TrackProductionOrder;
          subRows: ProductionRow[];
      }
    | { rowKey: string; isHeader: false; workOrder: TrackWorkOrder };

type OrderRow =
    | { rowKey: string; isHeader: true; mother: TrackMother; subRows: OrderRow[] }
    | { rowKey: string; isHeader: false; shipment: TrackShipment };

type StnRow =
    | { rowKey: string; isHeader: true; stn: TrackStn; subRows: StnRow[] }
    | { rowKey: string; isHeader: false; stn: TrackStn; item: TrackStnItem };

/** Origin cell: "Manual", or "Plan #XYZ" + the windows it was placed to serve. */
const originCell = (o?: TrackOrigin): ReactElement => {
    if (!o || o.source === 'MANUAL') {
        return (
            <Badge size="sm" color="gray" variant="light" tt="none">
                Manual
            </Badge>
        );
    }
    const shown = o.servesWindows.slice(0, 2).join(', ');
    return (
        <Stack gap={0}>
            <Badge size="sm" color="teal" variant="light" tt="none">
                Plan #{o.planId ?? '—'}
            </Badge>
            {shown && (
                <Text size="xs" c="dimmed">
                    for {shown}
                    {o.servesWindows.length > 2 ? '…' : ''}
                </Text>
            )}
        </Stack>
    );
};

const round = (v: number | undefined): string =>
    v == null ? '0' : formatCount(Math.round(v));

/** Earliest need-by across a mother's window breakdown (the demand target). */
const earliestNeedBy = (m: TrackMother): string => {
    const dates = (m.windowBreakdown ?? [])
        .map((w) => w.needByDate)
        .filter((d): d is string => !!d)
        .sort();
    return dates[0] ?? '—';
};

/**
 * Calculated ETA for a shipment: the actual delivered date once set, else the
 * planned (need-by) date. Kept simple until milestone-driven projection lands.
 */
const shipmentEta = (s: TrackShipment): string =>
    s.deliveryDate ?? s.plannedDate ?? '—';

const statCard = (label: string, value: string, hint?: string): ReactElement => (
    <Card withBorder padding="sm" radius="md">
        <Text size="xs" c="dimmed">
            {label}
        </Text>
        <Text fw={700} size="lg">
            {value}
        </Text>
        {hint && (
            <Text size="xs" c="dimmed">
                {hint}
            </Text>
        )}
    </Card>
);

const TrackView: FC = () => {
    const [data, setData] = useState<TrackOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tab, setTab] = useState<string>('orders');
    const [mother, setMother] = useState<TrackMother | null>(null);
    const [workOrder, setWorkOrder] = useState<TrackWorkOrder | null>(null);
    // Shipment form (the existing Orders AddShipmentModal, reused + pre-filled).
    const [shipOrder, setShipOrder] = useState<MotherOrder | null>(null);
    const [shipPrefill, setShipPrefill] = useState<{
        qtys?: Record<string, number>;
        plannedDate?: string | null;
        warehouseId?: string | null;
    }>({});
    const [shipBusy, setShipBusy] = useState(false);
    // Create-batch modal.
    const [batchWo, setBatchWo] = useState<TrackWorkOrder | null>(null);
    const [batchQty, setBatchQty] = useState<number>(0);
    const [batchCode, setBatchCode] = useState('');
    const [batchNotes, setBatchNotes] = useState('');
    // A batch is identified by its code AND its item, so the dates and the item belong on the
    // carve rather than being inherited from the order. Blank keeps the old behaviour exactly:
    // the item falls back to the work order's product.
    const [batchMfgDate, setBatchMfgDate] = useState('');
    const [batchExpiryDate, setBatchExpiryDate] = useState('');
    // Batch-shipment modal.
    const [shipBatch, setShipBatch] = useState<{ id: string; code: string; open: number } | null>(null);
    const [bsQty, setBsQty] = useState<number>(0);
    const [bsPlanned, setBsPlanned] = useState('');
    const [bsReadiness, setBsReadiness] = useState('');
    const [batchBusy, setBatchBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await getReplenishmentTrackOverview());
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Open the shipment form. `suggest` = AI/magic-wand: pre-fill pending qty per SKU
    // + the earliest need-by. Otherwise open a blank form for manual entry. Either way
    // uses the full mother order so the form has the proper line shape (in-prod caps).
    const openShipmentForm = useCallback(
        async (motherId: string, suggest: boolean) => {
            setShipBusy(true);
            setError(null);
            try {
                if (!suggest) {
                    setShipPrefill({});
                    setShipOrder(await getMotherOrder(motherId));
                    return;
                }
                const [full, sugg] = await Promise.all([
                    getMotherOrder(motherId),
                    getShipmentSuggestions(motherId),
                ]);
                const qtys: Record<string, number> = {};
                let earliest: string | null = null;
                for (const r of sugg.rows) {
                    qtys[r.itemId] = (qtys[r.itemId] ?? 0) + (r.suggestedQty ?? 0);
                    if (r.needByDate && (!earliest || r.needByDate < earliest)) {
                        earliest = r.needByDate;
                    }
                }
                setShipPrefill({
                    qtys,
                    plannedDate: earliest,
                    warehouseId: sugg.warehouseId,
                });
                setShipOrder(full);
            } catch (e) {
                setError((e as Error).message);
            } finally {
                setShipBusy(false);
            }
        },
        [],
    );

    const submitBatch = useCallback(async () => {
        if (!batchWo || batchQty <= 0) return;
        setBatchBusy(true);
        setError(null);
        try {
            await createWorkOrderBatch(batchWo.id, {
                quantity: batchQty,
                code: batchCode.trim() || undefined,
                notes: batchNotes.trim() || undefined,
                manufacturingDate: batchMfgDate.trim() || undefined,
                expiryDate: batchExpiryDate.trim() || undefined,
            });
            setBatchWo(null);
            setWorkOrder(null);
            await load();
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setBatchBusy(false);
        }
    }, [batchWo, batchQty, batchCode, batchNotes, batchMfgDate, batchExpiryDate, load]);

    // Qty already committed to batches (non-cancelled) for the open WO.
    const batchedQty = (wo: TrackWorkOrder): number =>
        wo.batches
            .filter((b) => b.status !== 'CANCELLED')
            .reduce((s, b) => s + (b.quantity ?? 0), 0);

    const submitBatchShipment = useCallback(async () => {
        if (!shipBatch || bsQty <= 0) return;
        if (!bsReadiness) {
            setError('Goods readiness date (GRD) is required — a future date is fine.');
            return;
        }
        setBatchBusy(true);
        setError(null);
        try {
            await createBatchShipment(shipBatch.id, {
                quantity: bsQty,
                plannedDate: bsPlanned || null,
                readinessDate: bsReadiness,
            });
            setShipBatch(null);
            setWorkOrder(null);
            await load();
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setBatchBusy(false);
        }
    }, [shipBatch, bsQty, bsPlanned, bsReadiness, load]);

    useEffect(() => {
        void load();
    }, [load]);

    /**
     * The buy instrument, in its own shape: the contract on top, the shipments carved from it
     * nested underneath. Each shipment IS a purchase-order row, so this is the same
     * header-and-lines reading as production and transfers.
     */
    const orderRows = useMemo<OrderRow[]>(
        () =>
            (data?.mothers ?? []).map((row) => ({
                rowKey: `mo:${row.id ?? row.orderNumber ?? ''}`,
                isHeader: true as const,
                mother: row,
                subRows: (row.shipments ?? []).map((shipment, index) => ({
                    rowKey: `po:${shipment.purchaseOrderId ?? index}`,
                    isHeader: false as const,
                    shipment,
                })),
            })),
        [data],
    );

    const motherCols = useMemo<DataSheetColumn<OrderRow>[]>(
        () => [
            {
                key: 'orderNumber',
                header: 'Order / shipment',
                width: 200,
                sticky: true,
                render: (_v, r) =>
                    r.isHeader ? (
                        <Stack gap={0}>
                            <Text fw={600} size="xs">
                                {r.mother.orderNumber ?? r.mother.id}
                            </Text>
                            <Text size="10px" c="dimmed">
                                {(r.mother.shipments ?? []).length} shipment
                                {(r.mother.shipments ?? []).length === 1 ? '' : 's'}
                            </Text>
                        </Stack>
                    ) : (
                        <Text size="xs">{r.shipment.purchaseOrderId}</Text>
                    ),
            },
            {
                key: 'vendorName',
                header: 'Vendor',
                width: 170,
                render: (_v, r) => (r.isHeader ? (r.mother.vendorName ?? '') : ''),
            },
            {
                key: 'origin',
                header: 'For',
                width: 150,
                render: (_v, r) =>
                    r.isHeader ? originCell(data?.originByMother[r.mother.id ?? '']) : null,
            },
            {
                key: 'orderedUnits',
                header: 'Ordered',
                width: 90,
                align: 'right',
                render: (_v, r) =>
                    round(r.isHeader ? r.mother.orderedUnits : r.shipment.totalUnits),
            },
            {
                key: 'inProductionUnits',
                header: 'In prod.',
                width: 90,
                align: 'right',
                render: (_v, r) => (r.isHeader ? round(r.mother.inProductionUnits) : ''),
            },
            {
                key: 'inTransitUnits',
                header: 'In transit',
                width: 95,
                align: 'right',
                render: (_v, r) => (r.isHeader ? round(r.mother.inTransitUnits) : ''),
            },
            {
                key: 'openUnits',
                header: 'Open',
                width: 80,
                align: 'right',
                render: (_v, r) => (r.isHeader ? round(r.mother.openUnits) : ''),
            },
            {
                key: 'receivedUnits',
                header: 'Received',
                width: 90,
                align: 'right',
                render: (_v, r) =>
                    round(r.isHeader ? r.mother.receivedUnits : r.shipment.receivedUnits),
            },
            {
                key: 'needBy',
                header: 'Need by',
                width: 120,
                render: (_v, r) =>
                    r.isHeader
                        ? earliestNeedBy(r.mother)
                        : (r.shipment.plannedDate ?? r.shipment.readinessDate ?? ''),
            },
            {
                key: 'status',
                header: 'Status',
                width: 130,
                render: (_v, r) => (
                    <Badge size="sm" variant="dot">
                        {String((r.isHeader ? r.mother.status : r.shipment.status) ?? '')}
                    </Badge>
                ),
            },
        ],
        [data],
    );

    /**
     * Production rows are the ORDER, with its SKU lines nested underneath — the shape the
     * instrument was actually placed in (one order, one factory, many SKUs). Work orders that
     * predate the always-a-header rule have no parent to sit under, so they surface as
     * top-level rows rather than disappearing.
     */
    const productionRows = useMemo<ProductionRow[]>(() => {
        const orders = (data?.productionOrders ?? []).map((order) => ({
            rowKey: `po:${order.id}`,
            isHeader: true as const,
            order,
            subRows: order.workOrders.map((wo) => ({
                rowKey: `wo:${wo.id}`,
                isHeader: false as const,
                workOrder: wo,
            })),
        }));
        const orphans = (data?.workOrders ?? [])
            .filter((wo) => !wo.productionOrderId)
            .map((wo) => ({
                rowKey: `wo:${wo.id}`,
                isHeader: false as const,
                workOrder: wo,
            }));
        return [...orders, ...orphans];
    }, [data]);

    const productionCols = useMemo<DataSheetColumn<ProductionRow>[]>(
        () => [
            {
                key: 'code',
                header: 'Production order / SKU',
                width: 230,
                sticky: true,
                render: (_v, r) =>
                    r.isHeader ? (
                        <Stack gap={0}>
                            <Text fw={600} size="xs">
                                {r.order.code}
                            </Text>
                            <Text size="10px" c="dimmed">
                                {r.order.lineCount} SKU{r.order.lineCount === 1 ? '' : 's'}
                            </Text>
                        </Stack>
                    ) : (
                        <Stack gap={0}>
                            <Text size="xs">{r.workOrder.productId}</Text>
                            <Text size="10px" c="dimmed">
                                {r.workOrder.code}
                            </Text>
                        </Stack>
                    ),
            },
            {
                key: 'where',
                header: 'Made at',
                width: 190,
                render: (_v, r) =>
                    r.isHeader ? (
                        <Text size="xs">
                            {r.order.productionLocationName}
                            {r.order.productionLocationType === 'VENDOR' ? ' (vendor)' : ''}
                        </Text>
                    ) : null,
            },
            {
                key: 'origin',
                header: 'For',
                width: 150,
                render: (_v, r) =>
                    r.isHeader ? null : originCell(data?.originByWorkOrder[r.workOrder.id]),
            },
            {
                key: 'quantity',
                header: 'Given',
                width: 90,
                align: 'right',
                render: (_v, r) =>
                    round(r.isHeader ? r.order.quantity : r.workOrder.quantity),
            },
            {
                key: 'inProductionUnits',
                header: 'In prod.',
                width: 90,
                align: 'right',
                render: (_v, r) =>
                    round(
                        r.isHeader ? r.order.inProductionUnits : r.workOrder.inProductionUnits,
                    ),
            },
            {
                key: 'batchCount',
                header: 'Batches',
                width: 90,
                align: 'right',
                render: (_v, r) => (r.isHeader ? '' : String(r.workOrder.batchCount ?? 0)),
            },
            {
                key: 'dueDate',
                header: 'Due',
                width: 120,
                render: (_v, r) =>
                    r.isHeader ? r.order.expectedCompletionDate : r.workOrder.dueDate,
            },
            {
                key: 'status',
                header: 'Status',
                width: 130,
                render: (_v, r) => (
                    <Badge size="sm" variant="dot">
                        {r.isHeader ? r.order.status : r.workOrder.status}
                    </Badge>
                ),
            },
        ],
        [data],
    );

    /** Transfers: the STN header with its item lines nested, same shape as the other two. */
    const stnRows = useMemo<StnRow[]>(
        () =>
            (data?.stns ?? []).map((stn) => ({
                rowKey: `stn:${stn.id}`,
                isHeader: true as const,
                stn,
                subRows: stn.items.map((item, index) => ({
                    rowKey: `stn:${stn.id}:${item.itemId}:${index}`,
                    isHeader: false as const,
                    stn,
                    item,
                })),
            })),
        [data],
    );

    const stnCols = useMemo<DataSheetColumn<StnRow>[]>(
        () => [
            {
                key: 'code',
                header: 'Transfer / item',
                width: 230,
                sticky: true,
                render: (_v, r) =>
                    r.isHeader ? (
                        <Stack gap={0}>
                            <Text fw={600} size="xs">
                                {r.stn.code}
                            </Text>
                            <Text size="10px" c="dimmed">
                                {r.stn.lineCount} item{r.stn.lineCount === 1 ? '' : 's'} ·{' '}
                                {r.stn.itemType}
                            </Text>
                        </Stack>
                    ) : (
                        <Text size="xs">{r.item?.itemId}</Text>
                    ),
            },
            {
                key: 'route',
                header: 'From → to',
                width: 260,
                render: (_v, r) =>
                    r.isHeader ? (
                        <Text size="xs">
                            {r.stn.fromNodeName} → {r.stn.toNodeName}
                        </Text>
                    ) : null,
            },
            {
                key: 'plannedQty',
                header: 'Planned',
                width: 95,
                align: 'right',
                render: (_v, r) => round(r.isHeader ? r.stn.plannedQty : r.item?.plannedQty),
            },
            {
                key: 'dispatchedQty',
                header: 'Dispatched',
                width: 105,
                align: 'right',
                render: (_v, r) =>
                    round(r.isHeader ? r.stn.dispatchedQty : r.item?.dispatchedQty),
            },
            {
                key: 'receivedQty',
                header: 'Received',
                width: 95,
                align: 'right',
                render: (_v, r) => round(r.isHeader ? r.stn.receivedQty : r.item?.receivedQty),
            },
            { key: 'createdAt', header: 'Raised', width: 110, render: (_v, r) => r.stn.createdAt },
            {
                key: 'status',
                header: 'Status',
                width: 130,
                render: (_v, r) =>
                    r.isHeader ? (
                        <Badge size="sm" variant="dot">
                            {r.stn.status}
                        </Badge>
                    ) : null,
            },
        ],
        [],
    );

    const s = data?.summary;

    return (
        <Stack gap="md" p="md">
            <Group justify="space-between">
                <Title order={4}>Track</Title>
                <Button variant="light" size="xs" onClick={load} loading={loading}>
                    Refresh
                </Button>
            </Group>

            {error && (
                <Card withBorder padding="sm" radius="md" bg="red.0">
                    <Text c="red" size="sm">
                        {error}
                    </Text>
                </Card>
            )}

            <SimpleGrid cols={{ base: 2, sm: 3, md: 7 }} spacing="sm">
                {statCard('In production', round(s?.inProductionUnits), 'FG + RM + WO')}
                {statCard('In transit', round(s?.inTransitUnits), 'GRD reached')}
                {statCard('Open', round(s?.openUnits), 'OPEN orders')}
                {statCard('Received', round(s?.receivedUnits))}
                {statCard('Orders', round(s?.motherCount))}
                {statCard('Production', round(s?.productionOrderCount), 'orders')}
                {statCard('Transfers', round(s?.stnCount))}
            </SimpleGrid>

            {loading ? (
                <Group justify="center" p="xl">
                    <Loader />
                </Group>
            ) : (
                <Tabs value={tab} onChange={(t) => setTab(t ?? 'orders')}>
                    <Tabs.List>
                        <Tabs.Tab value="orders">
                            Orders ({data?.mothers.length ?? 0})
                        </Tabs.Tab>
                        <Tabs.Tab value="work">
                            Production ({data?.productionOrders.length ?? 0})
                        </Tabs.Tab>
                        <Tabs.Tab value="transfers">
                            Transfers ({data?.stns.length ?? 0})
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="orders" pt="sm">
                        <DataSheet<OrderRow>
                            data={orderRows}
                            columns={motherCols}
                            getRowKey={(r) => r.rowKey}
                            getSubRows={(r) => (r.isHeader ? r.subRows : undefined)}
                            enableSearch
                            searchPlaceholder="Search order or vendor..."
                            maxHeight="calc(100vh - 420px)"
                            emptyState="No orders placed yet. Approve instruments on the “What do I need” tab."
                            onRowClick={(r) => {
                                if (r.isHeader) setMother(r.mother);
                            }}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="work" pt="sm">
                        <DataSheet<ProductionRow>
                            data={productionRows}
                            columns={productionCols}
                            getRowKey={(r) => r.rowKey}
                            getSubRows={(r) => (r.isHeader ? r.subRows : undefined)}
                            enableSearch
                            searchPlaceholder="Search order, work order or product..."
                            maxHeight="calc(100vh - 420px)"
                            emptyState="No production orders placed yet."
                            // Only a line has batches to ship — the header is a grouping.
                            onRowClick={(r) => {
                                if (!r.isHeader) setWorkOrder(r.workOrder);
                            }}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="transfers" pt="sm">
                        <DataSheet<StnRow>
                            data={stnRows}
                            columns={stnCols}
                            getRowKey={(r) => r.rowKey}
                            getSubRows={(r) => (r.isHeader ? r.subRows : undefined)}
                            enableSearch
                            searchPlaceholder="Search transfer or item..."
                            maxHeight="calc(100vh - 420px)"
                            emptyState="No transfers in flight."
                        />
                    </Tabs.Panel>
                </Tabs>
            )}

            <Drawer
                opened={mother !== null}
                onClose={() => setMother(null)}
                position="right"
                size="lg"
                title={mother ? `Order ${mother.orderNumber ?? mother.id}` : ''}
            >
                {mother && (
                    <Stack gap="md">
                        <Group gap="sm">
                            <Badge color={mother.orderType === 'OPEN' ? 'grape' : 'blue'}>
                                {mother.orderType ?? 'FIRM'}
                            </Badge>
                            <Text size="sm">{mother.vendorName ?? mother.vendorId}</Text>
                            {mother.warehouseId && (
                                <Text size="sm" c="dimmed">
                                    → {mother.warehouseId}
                                </Text>
                            )}
                        </Group>

                        <SimpleGrid cols={mother.orderType === 'OPEN' ? 4 : 4} spacing="xs">
                            {statCard('Ordered', round(mother.orderedUnits))}
                            {mother.orderType === 'OPEN'
                                ? statCard('Open', round(mother.openUnits))
                                : statCard('In production', round(mother.inProductionUnits))}
                            {statCard('In transit', round(mother.inTransitUnits))}
                            {statCard('Received', round(mother.receivedUnits))}
                        </SimpleGrid>

                        <div>
                            <Text fw={600} size="sm" mb={4}>
                                Windows ({(mother.windowBreakdown ?? []).length})
                            </Text>
                            <Stack gap={4}>
                                {(mother.windowBreakdown ?? []).map((w, i) => (
                                    <Group key={i} justify="space-between" wrap="nowrap">
                                        <Text size="sm">{w.itemId}</Text>
                                        <Text size="sm" c="dimmed">
                                            {w.windowKey}
                                        </Text>
                                        <Text size="sm">need by {w.needByDate ?? '—'}</Text>
                                        <Text size="sm" fw={600}>
                                            {round(w.quantity)}
                                        </Text>
                                    </Group>
                                ))}
                                {(mother.windowBreakdown ?? []).length === 0 && (
                                    <Text size="sm" c="dimmed">
                                        No window breakdown.
                                    </Text>
                                )}
                            </Stack>
                        </div>

                        <Group justify="space-between">
                            <Divider label="Shipments" labelPosition="left" style={{ flex: 1 }} />
                            <Group gap={4} wrap="nowrap">
                                <Button
                                    size="compact-xs"
                                    variant="light"
                                    loading={shipBusy}
                                    onClick={() =>
                                        mother.id && openShipmentForm(mother.id, false)
                                    }
                                >
                                    Create shipment
                                </Button>
                                <Tooltip
                                    label="Suggest shipments — pre-fills pending qty + need-by"
                                    withArrow
                                >
                                    <ActionIcon
                                        size="lg"
                                        variant="light"
                                        color="grape"
                                        loading={shipBusy}
                                        onClick={() =>
                                            mother.id && openShipmentForm(mother.id, true)
                                        }
                                    >
                                        ✨
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        </Group>
                        <Stack gap={6}>
                            {(mother.shipments ?? []).map((sh, i) => (
                                <Group key={i} justify="space-between" wrap="nowrap">
                                    <Badge size="sm" variant="dot">
                                        {sh.status ?? ''}
                                    </Badge>
                                    <Text size="xs" c="dimmed">
                                        need by {sh.plannedDate ?? '—'}
                                    </Text>
                                    <Text size="xs">ETA {shipmentEta(sh)}</Text>
                                    <Text size="sm" fw={600}>
                                        {round(sh.totalUnits)}
                                    </Text>
                                </Group>
                            ))}
                            {(mother.shipments ?? []).length === 0 && (
                                <Text size="sm" c="dimmed">
                                    No shipments yet.
                                </Text>
                            )}
                        </Stack>
                        <Text size="xs" c="dimmed">
                            “Create shipment” opens the shipment form pre-filled with the
                            pending quantity and the requirement’s need-by. The GRD
                            (goods readiness date) is required — a future GRD keeps the
                            shipment in production until the date is reached, then it
                            moves to in-transit automatically.
                        </Text>
                    </Stack>
                )}
            </Drawer>

            <AddShipmentModal
                order={shipOrder}
                opened={shipOrder !== null}
                onClose={() => setShipOrder(null)}
                onAdded={() => {
                    setShipOrder(null);
                    void load();
                }}
                prefill={shipPrefill}
            />

            <Drawer
                opened={workOrder !== null}
                onClose={() => setWorkOrder(null)}
                position="right"
                size="md"
                title={workOrder ? `Work order ${workOrder.code}` : ''}
            >
                {workOrder && (
                    <Stack gap="md">
                        <Text size="sm">{workOrder.productId}</Text>
                        <SimpleGrid cols={3} spacing="xs">
                            {statCard('Given', round(workOrder.quantity))}
                            {statCard('In production', round(workOrder.inProductionUnits))}
                            {statCard('Batches', round(workOrder.batchCount))}
                        </SimpleGrid>
                        <Group justify="space-between">
                            <Divider label="Batches" labelPosition="left" style={{ flex: 1 }} />
                            <Button
                                size="compact-xs"
                                variant="light"
                                onClick={() => {
                                    setBatchQty(
                                        Math.max(
                                            0,
                                            workOrder.quantity - batchedQty(workOrder),
                                        ),
                                    );
                                    setBatchCode('');
                                    setBatchNotes('');
                                    setBatchWo(workOrder);
                                }}
                            >
                                Create batch
                            </Button>
                        </Group>
                        <Stack gap={6}>
                            {workOrder.batches.map((b) => (
                                <Group key={b.id} justify="space-between" wrap="nowrap">
                                    <Text size="sm" fw={600}>
                                        {b.code}
                                    </Text>
                                    <Badge size="sm" variant="dot">
                                        {b.status}
                                    </Badge>
                                    <Text size="xs" c="dimmed">
                                        qty {round(b.quantity)} · open {round(b.open)}
                                    </Text>
                                    {b.status === 'IN_PROGRESS' && b.open > 0 && (
                                        <Button
                                            size="compact-xs"
                                            variant="subtle"
                                            onClick={() => {
                                                setBsQty(b.open);
                                                setBsPlanned('');
                                                setBsReadiness('');
                                                setShipBatch({
                                                    id: b.id,
                                                    code: b.code,
                                                    open: b.open,
                                                });
                                            }}
                                        >
                                            Create shipment
                                        </Button>
                                    )}
                                </Group>
                            ))}
                            {workOrder.batches.length === 0 && (
                                <Text size="sm" c="dimmed">
                                    No batches yet. A batch's open qty is in-production;
                                    ship it to move it into in-transit once its GRD is
                                    reached.
                                </Text>
                            )}
                        </Stack>
                    </Stack>
                )}
            </Drawer>

            {/* Create batch */}
            <Modal
                opened={batchWo !== null}
                onClose={() => setBatchWo(null)}
                title={batchWo ? `New batch — ${batchWo.code}` : ''}
                centered
                size="md"
            >
                {batchWo && (
                    <Stack gap="md">
                        <SimpleGrid cols={2} spacing="xs">
                            {statCard('Product (SKU)', batchWo.productId)}
                            {statCard(
                                'Pending for batches',
                                round(
                                    Math.max(0, batchWo.quantity - batchedQty(batchWo)),
                                ),
                                `WO ${round(batchWo.quantity)} − batched ${round(batchedQty(batchWo))}`,
                            )}
                        </SimpleGrid>
                        <div>
                            <Text size="sm" mb={4}>
                                Batch number
                            </Text>
                            <TextInput
                                placeholder={`Auto — ${batchWo.code}-B${batchWo.batchCount + 1}`}
                                value={batchCode}
                                onChange={(e) => setBatchCode(e.currentTarget.value)}
                            />
                        </div>
                        <div>
                            <Text size="sm" mb={4}>
                                Quantity
                            </Text>
                            <NumberInput
                                min={0}
                                max={Math.max(0, batchWo.quantity - batchedQty(batchWo))}
                                value={batchQty}
                                onChange={(v) => setBatchQty(Number(v) || 0)}
                            />
                        </div>
                        <SimpleGrid cols={2} spacing="sm">
                            <div>
                                <Text size="sm" mb={4}>
                                    Made
                                </Text>
                                <TextInput
                                    placeholder="YYYY-MM-DD"
                                    value={batchMfgDate}
                                    onChange={(e) => setBatchMfgDate(e.currentTarget.value)}
                                />
                            </div>
                            <div>
                                <Text size="sm" mb={4}>
                                    Expires
                                </Text>
                                <TextInput
                                    placeholder="YYYY-MM-DD"
                                    value={batchExpiryDate}
                                    onChange={(e) => setBatchExpiryDate(e.currentTarget.value)}
                                />
                            </div>
                        </SimpleGrid>
                        <div>
                            <Text size="sm" mb={4}>
                                Notes
                            </Text>
                            <TextInput
                                value={batchNotes}
                                onChange={(e) => setBatchNotes(e.currentTarget.value)}
                                placeholder="Optional"
                            />
                        </div>
                        <Group justify="flex-end">
                            <Button
                                variant="default"
                                size="xs"
                                onClick={() => setBatchWo(null)}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="xs"
                                loading={batchBusy}
                                disabled={batchQty <= 0}
                                onClick={submitBatch}
                            >
                                Create batch
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Modal>

            {/* Create shipment for a batch */}
            <Modal
                opened={shipBatch !== null}
                onClose={() => setShipBatch(null)}
                title={shipBatch ? `Ship batch — ${shipBatch.code}` : ''}
                centered
                size="md"
            >
                <Stack gap="md">
                    <div>
                        <Text size="sm" mb={4}>
                            Quantity (open {round(shipBatch?.open)})
                        </Text>
                        <NumberInput
                            min={0}
                            max={shipBatch?.open}
                            value={bsQty}
                            onChange={(v) => setBsQty(Number(v) || 0)}
                        />
                    </div>
                    <div>
                        <Text size="sm" mb={4}>
                            Expected date (need-by)
                        </Text>
                        <DateSinglePicker
                            value={bsPlanned}
                            onChange={(v) => setBsPlanned(v || '')}
                            placeholder="When it's needed"
                            clearable
                        />
                    </div>
                    <div>
                        <Text size="sm" mb={4}>
                            Goods readiness date — GRD *
                        </Text>
                        <DateSinglePicker
                            value={bsReadiness}
                            onChange={(v) => setBsReadiness(v || '')}
                            placeholder="Required — future date OK"
                        />
                    </div>
                    <Group justify="flex-end">
                        <Button variant="default" size="xs" onClick={() => setShipBatch(null)}>
                            Cancel
                        </Button>
                        <Button
                            size="xs"
                            loading={batchBusy}
                            disabled={bsQty <= 0 || !bsReadiness}
                            onClick={submitBatchShipment}
                        >
                            Create shipment
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};

export default TrackView;
